import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  Modal,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import StyledSelect from "../all-products/components/custom/StyledSelect";
import { getManagers, validateManagerPin } from "../../../../config/Services";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import StyledButton from "../all-products/components/custom/StyledButton";
import { getFromLocalStorage } from "../../../../utils/localStorageHelpers";
import { useSnackbar } from "./SnackbarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import useGetDevice from "./useGetDevice";
import { UserContext } from "../../../../App";
import { useDispatch } from "react-redux";
import { addManager } from "../all-products/redux/Actions/SessionActions";
import "./useAuthModal.css";

const useAuthModal = () => {
  const showSnackbar = useSnackbar();
  const isMobile = useGetDevice();
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [managerNumber, setManagerNumber] = useState("");
  const [managerPin, setManagerPin] = useState("");
  const [callback, setCallback] = useState(null);
  const [isVisible, setVisible] = useState(false);
  const locId = getFromLocalStorage("locId");
  const { color } = useContext(UserContext);

  const style = {
    backgroundColor: color === "dark" ? "#353a50" : "#fff",
    color: color === "dark" ? "white" : "black",
    padding: isMobile ? "5%" : "2%",
    borderRadius: "10px",
    width: isMobile ? "100%" : "50%",
    maxWidth: "100vw",
    maxHeight: "100%",
    position: "fixed",
    top: "50%",
    left: "0",
    transform: `translate(${isMobile ? `0%` : `50%`}, -50%)`,
    overflowY: "auto",
  };

  const handleClose = () => setOpenModal(false);
  const toggle = () => setVisible((prev) => !prev);

  const { data: managerList, isLoading: isManagerListLoading } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["managers"],
    queryFn: async () => {
      const payload = { contextId: locId };
      const response = await getManagers(payload);
      return response?.data?.data?.map((e) => {
        return {
          value: e?.mobile_number,
          label: e?.first_name,
          uidx: e?.uidx,
        };
      });
    },
    enabled: openModal,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  const { mutate: validateManager, isLoading: isValidating } = useMutation({
    mutationKey: "validateManager",
    mutationFn: async (payload) => {
      const response = await validateManagerPin(payload);
      if (response?.data?.data?.status === "ERROR") {
        throw new Error(response?.data?.data?.message);
      }
      setOpenModal(false);
      callback();
      return response;
    },
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  const [pinMaxLength, setPinMaxLength] = useState(null);

  const openAuthModal = (callback, cashierPin = false, options = {}) => {
    if (!callback) {
      throw new Error("Callback is required");
    }
    setOpenModal(true);
    setCallback(() => callback);
    if (options.maxLength) {
      setPinMaxLength(options.maxLength);
    } else {
      setPinMaxLength(null);
    }
  };

  const handleManagerChange = (e) => {
    setManagerNumber(e.value);
    dispatch(addManager(e));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const authPayload = {
      mobile: managerNumber,
      primaryPassword: managerPin,
    };
    validateManager(authPayload);
  };

  const renderAuthModal = () => (
    <Modal
      open={openModal}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        "& > .MuiBackdrop-root": {
          backdropFilter: "blur(5px)",
        },
      }}
    >
      <Box className={`cash-drawer-modal-container ${color === "dark" ? "dark" : ""}`}>
        {/* Close button top-right */}
        <button type="button" className="cash-drawer-close-btn" onClick={handleClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="cash-drawer-close-icon"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Header */}
        <div className="cash-drawer-header">
          <div className="cash-drawer-icon-wrap">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="cash-drawer-lock-icon"
            >
              <circle cx="12" cy="16" r="1"></circle>
              <rect x="3" y="10" width="18" height="12" rx="2"></rect>
              <path d="M7 10V7a5 5 0 0 1 10 0v3"></path>
            </svg>
          </div>
          <h3 className="cash-drawer-title">Select Manager</h3>
          <p className="cash-drawer-subtitle">Manager PIN verification required</p>
        </div>

        {/* Form Body */}
        <div className="cash-drawer-body">
          <form onSubmit={handleSubmit} className="cash-drawer-form">
            {/* Select Manager Field */}
            <div className="cash-drawer-field">
              <label className="cash-drawer-label">Select manager</label>
              <StyledSelect autoFocus onChange={handleManagerChange} options={managerList} />
            </div>

            {/* PIN Input Field */}
            <div className="cash-drawer-field">
              <label className="cash-drawer-label">Pin</label>
              <FormControl fullWidth variant="outlined">
                <div className="cash-drawer-input-wrapper">
                  <Input
                    id="outlined-adornment-password"
                    type={isVisible ? "text" : "password"}
                    value={managerPin}
                    onChange={(e) => {
                      if (pinMaxLength && e.target.value.length > pinMaxLength) return;
                      setManagerPin(e.target.value);
                    }}
                    className="cash-drawer-input"
                    inputProps={{ inputMode: "numeric", maxLength: pinMaxLength || undefined }}
                    autoComplete="new-password"
                    placeholder="Pin"
                    disableUnderline
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          tabIndex={-1}
                          aria-label="toggle password visibility"
                          onClick={toggle}
                          edge="end"
                          size="small"
                        >
                          {isVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </div>
              </FormControl>
            </div>

            {/* Action buttons */}
            <div className="cash-drawer-actions">
              <button type="button" className="cash-drawer-cancel-btn" onClick={handleClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="cash-drawer-submit-btn"
                disabled={managerPin.length < 4 || !managerNumber || isValidating}
                onClick={handleSubmit}
              >
                {isValidating ? (
                  <CircularProgress color="success" sx={{ color: "#fff" }} size={20} />
                ) : (
                  "Proceed"
                )}
              </button>
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );

  return { renderAuthModal, openAuthModal };
};

export default useAuthModal;
